import { Component } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { AuthService } from '../../services/auth.service';
import { OrganisationService } from '../../services/organisation.service';
import { ProductService } from '../../services/product.service';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html',
  styleUrl: './products.component.scss',
})
export class ProductsComponent {
  /** Page-level monthly/annual switch, passed down to every product card.
   *  Remembered per browser: the page component is recreated on every
   *  navigation, so without this the choice fell back to monthly each time
   *  the user came back. */
  billingInterval: 'month' | 'year' = ProductsComponent.readStoredInterval();

  private static readonly STORAGE_KEY = 'al-billing-interval';

  private static readStoredInterval(): 'month' | 'year' {
    try {
      return localStorage.getItem(ProductsComponent.STORAGE_KEY) === 'year'
        ? 'year'
        : 'month';
    } catch {
      // Private mode / blocked storage — monthly is the safe default.
      return 'month';
    }
  }

  setBillingInterval(interval: 'month' | 'year'): void {
    this.billingInterval = interval;
    try {
      localStorage.setItem(ProductsComponent.STORAGE_KEY, interval);
    } catch {
      // Not being able to remember it must not break the switch.
    }
  }

  constructor(
    protected productService: ProductService,
    protected organisationService: OrganisationService,
    protected dialogService: NbDialogService,
    protected authService: AuthService,
    protected t: TranslateService
  ) {
    // this.productService.listProducts().subscribe();
    // this.organisationService.get().subscribe();

    this.productService.getActivePlan().subscribe();
  }

  get activeProduct() {
    return this.productService.activeProduct$.value;
  }

  /** Individueller Vertrag: Hinweis statt Planauswahl, Trial und Kuendigung. */
  get isContract(): boolean {
    return this.productService.isContract;
  }

  get contractName(): string {
    return this.productService.currentPlan$.value?.current_plan?.display_name || '';
  }

  cancelSubscription() {
    // open dialog

    // Cancellation falls back to the free plan (30d analytics retention) —
    // warn whenever the current plan keeps data longer.
    const FREE_PLAN_RETENTION_DAYS = 30;
    const currentRetention = this.activeProduct?.retentionDays;
    let message = this.t.instant(
      'dialogs.confirmDialog.cancelSubscription.message'
    );
    if (currentRetention != null && currentRetention > FREE_PLAN_RETENTION_DAYS) {
      message +=
        ' ' +
        this.t.instant('dialogs.confirmDialog.planRetentionDowngrade.cancelHint', {
          currentDays: currentRetention,
          targetDays: FREE_PLAN_RETENTION_DAYS,
        });
    }

    const confirmDialogRef = this.dialogService.open(ConfirmDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        title: 'dialogs.confirmDialog.cancelSubscription.title',
        message,
      },
    });

    // Delete the project if the user confirms
    confirmDialogRef.onClose.subscribe(async (isConfirmed: boolean) => {
      if (isConfirmed) {
        this.productService.cancelSubscription().subscribe(() => {
          window.location.reload();
        });
      }
    });
  }

  get products() {
    return this.productService.products$.value.filter(
      (p) => p.isVisible !== false
    );
  }
}
